/**
 * AuthContext wires the module-level authStore into the React tree so
 * components re-render when sign-in/out happens. The store is still the
 * source of truth — this context just publishes ticks.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getAccessToken,
  getClaims,
  registerSignOutCallback,
  setAccessToken,
  signOut as storeSignOut,
} from './authStore';
import { authApi } from '@/api/auth';
import type { OperatorMeResponse } from '@/api/types';
import type { JwtPayload } from '@/utils/jwt';

export type AuthStatus = 'booting' | 'authed' | 'unauthed';

export interface CurrentOperator {
  email: string;
  name: string | undefined;
  role: 'SAAS_ADMIN' | 'SUPER_ADMIN';
  isSuperAdmin: boolean;
}

interface AuthContextValue {
  status: AuthStatus;
  operator: CurrentOperator | null;
  setAuthed: (token: string, expiresAt: string | null) => void;
  setUnauthed: () => void;
  signOut: () => void;
  /**
   * Refine the claims-derived operator identity against `GET /api/auth/me`
   * (SASADM-0081 U3). Called at sign-in moments only. Fails soft — on any
   * error (incl. the pre-0133 `401`) the claims-derived identity is kept and
   * the operator is NEVER signed out.
   */
  refineOperator: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function deriveOperator(claims: JwtPayload | null): CurrentOperator | null {
  if (!claims || !claims.platformAdmin) return null;
  const role = claims.platformAdminRole ?? 'SAAS_ADMIN';
  return {
    email: claims.email ?? claims.sub ?? '',
    name: claims.name,
    role,
    isSuperAdmin: role === 'SUPER_ADMIN',
  };
}

/**
 * Map the authoritative `GET /api/auth/me` response (SASADM-0081 U3) onto the
 * `CurrentOperator` shape. Defaults a missing `platformAdminRole` to
 * `'SAAS_ADMIN'` (the least-privileged role), matching `deriveOperator`.
 */
function operatorFromMe(me: OperatorMeResponse): CurrentOperator {
  const role = me.platformAdminRole ?? 'SAAS_ADMIN';
  return {
    email: me.email,
    name: me.name ?? undefined,
    role,
    isSuperAdmin: role === 'SUPER_ADMIN',
  };
}

export interface AuthProviderProps {
  children: ReactNode;
  initialStatus?: AuthStatus;
}

export function AuthProvider({ children, initialStatus = 'booting' }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>(initialStatus);
  const [operator, setOperator] = useState<CurrentOperator | null>(() =>
    deriveOperator(getClaims()),
  );

  const refreshFromStore = useCallback(() => {
    const token = getAccessToken();
    setStatus(token ? 'authed' : 'unauthed');
    setOperator(deriveOperator(getClaims()));
  }, []);

  useEffect(() => {
    registerSignOutCallback(() => {
      setStatus('unauthed');
      setOperator(null);
    });
    return () => registerSignOutCallback(null);
  }, []);

  const setAuthed = useCallback(
    (token: string, expiresAt: string | null) => {
      setAccessToken(token, expiresAt);
      refreshFromStore();
    },
    [refreshFromStore],
  );

  const setUnauthed = useCallback(() => {
    setAccessToken(null);
    setStatus('unauthed');
    setOperator(null);
  }, []);

  const handleSignOut = useCallback(() => {
    storeSignOut();
    setStatus('unauthed');
    setOperator(null);
  }, []);

  // Refine the claims-derived identity against GET /api/auth/me. Called only at
  // sign-in moments (AuthReturn adoption + bootRefresh success, AuthGate boot),
  // NOT on a bare AuthProvider mount — so the many tests that mount AuthProvider
  // directly never trigger it. Fails soft: on any error (incl. the pre-0133
  // 401) the claims-derived identity is kept and the operator is never signed out.
  const refineOperator = useCallback(async () => {
    try {
      const me = await authApi.me();
      setOperator(operatorFromMe(me));
    } catch {
      // /me not yet available for a PLATFORM principal (pre-0133 401), network,
      // etc. — keep the claims-derived identity; never sign out.
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      operator,
      setAuthed,
      setUnauthed,
      signOut: handleSignOut,
      refineOperator,
    }),
    [status, operator, setAuthed, setUnauthed, handleSignOut, refineOperator],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function useCurrentOperator(): CurrentOperator | null {
  return useAuth().operator;
}

export function useAuthStatus(): AuthStatus {
  return useAuth().status;
}
